import { Component, Inject } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CustomFieldDto } from 'src/app/data-transfer/entities/custom-field-dto';
import { CustomFieldAssignmentsProfileDto, DocProfileDto } from 'src/app/data-transfer/entities/doc-entities/doc-profile-dto';
import { EvidenceTypeDto } from 'src/app/data-transfer/core-data/doc/evidence-type-dto';
import { DialogActions } from 'src/app/model/dictionary';

export class DocProfileDialogData {
  constructor(
    public readonly profile: DocProfileDto,
    public readonly isEditingNew: boolean,
    public readonly existingProfileNames: string[],
    public readonly evidenceTypes: EvidenceTypeDto[],
    public readonly availableSystemCustomFields: CustomFieldDto[],
    public readonly availableUnitCustomFields: CustomFieldDto[]
  ) {}
}

@Component({
  selector: 'app-doc-profile-dialog',
  templateUrl: './doc-profile-dialog.component.html',
  styleUrls: ['./doc-profile-dialog.component.scss'],
})
export class DocProfileDialogComponent {
  readonly sections: readonly number[] = [1, 8];

  nameControl = new FormControl('', { nonNullable: true, validators: [Validators.required] });
  searchControl = new FormControl('', { nonNullable: true });
  evidenceTypeControl = new FormControl<string[]>([], { nonNullable: true });
  systemFieldSelections: Record<number, number[]> = { 1: [], 8: [] };
  unitFieldSelections: Record<number, number[]> = { 1: [], 8: [] };

  private readonly expandedSections = new Set<string>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DocProfileDialogData,
    private dialogRef: MatDialogRef<DocProfileDialogComponent>
  ) {
    this.nameControl.setValue(data.profile.profileName);
    this.evidenceTypeControl.setValue([...data.profile.excludedEvidenceTypes]);

    for (const section of this.sections) {
      const selectedIds = this.decodeSelectedIds(section === 1 ? data.profile.customFieldsForSection1 : data.profile.customFieldsForSection8);
      this.systemFieldSelections[section] = data.availableSystemCustomFields.filter(field => selectedIds.has(field.id)).map(field => field.id);
      this.unitFieldSelections[section] = data.availableUnitCustomFields.filter(field => selectedIds.has(field.id)).map(field => field.id);
    }
  }

  private decodeSelectedIds(assignments: CustomFieldAssignmentsProfileDto | null): Set<number> {
    return new Set((assignments?.fields ?? []).filter(field => field.includeField).map(field => field.customFieldId));
  }

  private buildSectionAssignments(section: number): CustomFieldAssignmentsProfileDto {
    const selectedIds = new Set([...this.systemFieldSelections[section], ...this.unitFieldSelections[section]]);
    return { fields: [...selectedIds].map(customFieldId => ({ customFieldId, includeField: true })) };
  }

  isSystemFieldSelected(section: number, id: number): boolean {
    return this.systemFieldSelections[section].includes(id);
  }

  toggleSystemField(section: number, id: number): void {
    this.toggleId(this.systemFieldSelections[section], id);
  }

  isUnitFieldSelected(section: number, id: number): boolean {
    return this.unitFieldSelections[section].includes(id);
  }

  toggleUnitField(section: number, id: number): void {
    this.toggleId(this.unitFieldSelections[section], id);
  }

  private toggleId(ids: number[], id: number): void {
    const index = ids.indexOf(id);
    if (index === -1) {
      ids.push(id);
    } else {
      ids.splice(index, 1);
    }
  }

  private sectionKey(tab: 'system' | 'unit', section: number): string {
    return `${tab}-${section}`;
  }

  isSectionExpanded(tab: 'system' | 'unit', section: number): boolean {
    return !!this.searchControl.value.trim() || this.expandedSections.has(this.sectionKey(tab, section));
  }

  onSectionExpandedChange(tab: 'system' | 'unit', section: number, expanded: boolean): void {
    const key = this.sectionKey(tab, section);
    if (expanded) {
      this.expandedSections.add(key);
    } else {
      this.expandedSections.delete(key);
    }
  }

  filteredSystemFields(): CustomFieldDto[] {
    return this.filterFields(this.data.availableSystemCustomFields);
  }

  filteredUnitFields(): CustomFieldDto[] {
    return this.filterFields(this.data.availableUnitCustomFields);
  }

  private filterFields(fields: CustomFieldDto[]): CustomFieldDto[] {
    const query = this.searchControl.value.trim().toLowerCase();
    if (!query) return fields;
    return fields.filter(field => field.name.toLowerCase().includes(query));
  }

  save(): void {
    this.nameControl.markAsTouched();
    const trimmedName = this.nameControl.value.trim();
    if (!trimmedName) {
      this.nameControl.setErrors({ required: true });
      return;
    }
    if (this.data.existingProfileNames.includes(trimmedName)) {
      this.nameControl.setErrors({ duplicateName: true });
      return;
    }

    const savedProfile: DocProfileDto = {
      ...this.data.profile,
      profileName: trimmedName,
      customFieldsForSection1: this.buildSectionAssignments(1),
      customFieldsForSection8: this.buildSectionAssignments(8),
      excludedEvidenceTypes: this.evidenceTypeControl.value,
    };
    this.dialogRef.close({ event: DialogActions.CONFIRM, data: savedProfile });
  }

  closeDialog(): void {
    this.dialogRef.close({ event: DialogActions.REJECT });
  }
}
